import React from "react";
import styled from "styled-components";
import {
    ActivateFeatureButton,
    Button,
    CardTableRows,
    CardTableSelectAllControl,
    CardTableShell,
    CardTableToolbar,
    FlexContainer,
    LottieAnimation,
    PortalEmptyState,
    PortalSelect,
    PortalTableActions,
    PortalTableControls,
} from "src/components";
import { CLIENT_NOTICEBOARD, NOTICEBOARD } from "src/constants/permissions";
import Checkbox from "src/scenes/EmployeeList/components/Checkbox";
import NoticeboardContent from "./NoticeboardContent";
import PurpleLottieJson from "./../assets/purple-lottie.json";
import { FILTER_BY, FILTER_NOTICE } from "./../Noticeboard";
import pluralize from "pluralize";
import { capitalizeString, customNames } from "src/utils/helpers";
export const NOTICEBOARD_TABLE_WIDTH = "98.625rem";

const UpperButton = styled(Button)`
    border-radius: 8px !important;
    border: ${p => p.border || "none"};
    height: 42px;
    color: ${p => p.color || "var(--calendar-action-primary-text, #fff)"};
    background-color: ${p => p.backgroundColor || (p.disabled ? "var(--calendar-border-strong, #C9D2E7)" : "var(--calendar-action-primary-bg, #5B6EF5)")};
    box-sizing: border-box;
    font-size: 0.875rem;
    font-weight: 700;
    margin: ${p => p.margin};
    padding: ${p => p.padding};
    display: flex;
    align-items: center;
`;

const DescriptionLabel = styled.div`
    color: var(--calendar-text, #171A22);
    text-align: left;
    font-size: 1rem;
    font-weight: 600;
`;

const NoPostsContent = styled.div`
    display: grid;
    gap: 0.2rem;
`;

const InputSelector = styled(PortalSelect)`
    width: ${p => p.width || "12.5rem"};
`;

const ControlPanel = styled(CardTableToolbar)``;

const SelectedNoticeboardControl = styled.label`
    display: inline-flex;
    align-items: center;
    gap: 0.75rem;
    color: var(--calendar-text, #171A22);
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 800;

    @media (max-width: 760px) {
        align-items: stretch;
        flex-direction: column;
        gap: 0.35rem;
    }
`;

const NoticeboardTable = ({
    user,
    usersArray,
    requestDeleteNoticesConfirmation,
    checkedNotices,
    handleCheckboxChange,
    handleCheckAllNotices,
    checkAllNotices,
    isFetching,
    handleEditNotice,
    handleNewNotice,
    filterByOption,
    handleFilterByChange,
    handleOpenNoticeFilters,
    activeNoticeFilterCount,
    handleShowNoticeViewershipModal,
    handleFilterChangeByUserType,
    filterByUserTypeOption,
    employeesCounter,
    clientsCounter
}) => {
    const userHasClientManagement = user.permissions.includes(CLIENT_NOTICEBOARD);
    const userHasNoticeboardPermission = user.permissions.includes(NOTICEBOARD);
    const userCanManageSelectedNoticeboard = filterByUserTypeOption === FILTER_NOTICE.CLIENT ?
        userHasClientManagement :
        userHasNoticeboardPermission;

    return (
        <FlexContainer>
            <CardTableShell maxWidth={NOTICEBOARD_TABLE_WIDTH}>
                <ControlPanel>
                    <PortalTableControls>
                        { (usersArray && usersArray.length > 0) && !isFetching &&
                            <CardTableSelectAllControl>
                                <Checkbox
                                    key="selectAll"
                                    readOnly={false}
                                    checked={checkAllNotices}
                                    onClick={ () => handleCheckAllNotices(usersArray) }
                                />
                            </CardTableSelectAllControl>
                        }
                        <InputSelector value={filterByOption} onChange={handleFilterByChange}>
                            <option value={FILTER_BY.ALL_NOTICES}>All Posts</option>
                            <option value={FILTER_BY.ACTIVE_POSTS}>Active Posts</option>
                            <option value={FILTER_BY.SCHEDULED_POSTS}>Scheduled Posts</option>
                        </InputSelector>
                        <SelectedNoticeboardControl>
                            <span>
                                Selected Noticeboard:
                            </span>
                            <InputSelector
                                value={filterByUserTypeOption}
                                onChange={ (e) => handleFilterChangeByUserType(e.target.value)}
                                disabled={Number(userHasNoticeboardPermission) + Number(userHasClientManagement) < 2}
                                width="16rem"
                                style={{ flexShrink: 0 }}
                            >
                                {userHasNoticeboardPermission && (
                                    <option value={FILTER_NOTICE.EMPLOYEE}>
                                        {capitalizeString(pluralize.singular(customNames.employees))} Noticeboard ({employeesCounter})
                                    </option>
                                )}
                                {userHasClientManagement && (
                                    <option value={FILTER_NOTICE.CLIENT}>
                                        {capitalizeString(pluralize.singular(customNames.clients))} Noticeboard ({clientsCounter})
                                    </option>
                                )}
                            </InputSelector>
                        </SelectedNoticeboardControl>
                        <UpperButton
                            type="button"
                            onClick={handleOpenNoticeFilters}
                        >
                            Filter{activeNoticeFilterCount ? ` (${activeNoticeFilterCount})` : ""}
                        </UpperButton>
                        { usersArray.length > 0 && !isFetching && userCanManageSelectedNoticeboard &&
                        <>
                            { checkedNotices.length > 0 &&
                                <UpperButton
                                    backgroundColor="#ff7171"
                                    margin="0 2em 0 0"
                                    padding="0.625rem 0.938rem 0.625rem"
                                    onClick={() => {
                                        requestDeleteNoticesConfirmation(checkedNotices);
                                    }}
                                >
                                    Delete Selected Posts
                                </UpperButton>
                            }
                        </>
                        }
                    </PortalTableControls>

                    <PortalTableActions>
                        {!isFetching &&
                        <ActivateFeatureButton
                            width="12.5rem"
                            onClick={handleNewNotice}
                            disabled={
                                (filterByUserTypeOption === FILTER_NOTICE.CLIENT && !userHasClientManagement) ||
                                (filterByUserTypeOption === FILTER_NOTICE.EMPLOYEE && !userHasNoticeboardPermission)
                            }
                        >
                            New Post
                        </ActivateFeatureButton>
                        }
                    </PortalTableActions>
                </ControlPanel>

                <CardTableRows maxHeight="none" minHeight="25rem">
                    {
                        isFetching &&
                        <FlexContainer
                            justifyContent="center"
                            alignItems="center"
                            style={{ height: "25rem" }}>
                            <PurpleLoadingIndicator/>
                        </FlexContainer>
                    }

                    {!isFetching && usersArray.length === 0 && <NoPostAvailable/>}

                    {!isFetching && usersArray.length !== 0 &&
                        <NoticeboardContent
                            user={user}
                            filterByUserTypeOption={filterByUserTypeOption}
                            noticeArray={usersArray}
                            checkedNotices={checkedNotices}
                            handleCheckboxChange={handleCheckboxChange}
                            handleEditNotice={handleEditNotice}
                            fetching={null}
                            handleShowNoticeViewershipModal={handleShowNoticeViewershipModal}
                        />
                    }
                </CardTableRows>
            </CardTableShell>
        </FlexContainer>
    );
};

function NoPostAvailable() {
    return (
        <PortalEmptyState minHeight="18rem">
            <NoPostsContent>
                <DescriptionLabel>You have no posts available on your business Noticeboard.</DescriptionLabel>
                <DescriptionLabel>Send your first post to your {capitalizeString(pluralize(customNames.employees))} or {capitalizeString(pluralize(customNames.clients))} by selecting the ‘New Post’ button.</DescriptionLabel>
            </NoPostsContent>
        </PortalEmptyState>
    );
}

function PurpleLoadingIndicator() {
    return (
        <FlexContainer>
            <LottieAnimation animationJsonData={PurpleLottieJson} />
        </FlexContainer>
    );
}
export default NoticeboardTable;
